import React, { useState, useEffect, useRef } from 'react';
import { Ticket } from '../../../entities/ticket';
import * as S from './ExternalDataTabs.styles';
import { TournamentUserTab } from './tabs/TournamentUserTab';
import { TournamentPlayerProfileTab } from './tabs/TournamentPlayerProfileTab';
import { TournamentStatisticsTab } from './tabs/TournamentStatisticsTab';
import { TournamentTeamsTab } from './tabs/TournamentTeamsTab';
import { TournamentTicketsTab } from './tabs/TournamentTicketsTab';
import { HudleUserTab } from './tabs/HudleUserTab';
import { HudleServersTab } from './tabs/HudleServersTab';
import { HudleChannelsTab } from './tabs/HudleChannelsTab';
import { HudleFriendsTab } from './tabs/HudleFriendsTab';
import { HudleTicketsTab } from './tabs/HudleTicketsTab';
import { HudleActivityTab } from './tabs/HudleActivityTab';
import { HudleIpAddressesTab } from './tabs/HudleIpAddressesTab';

interface ExternalDataTabsProps {
  ticket: Ticket;
}

export const ExternalDataTabs: React.FC<ExternalDataTabsProps> = ({ ticket }) => {
  const [activeTab, setActiveTab] = useState<string>('user');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const externalData = ticket.client.externalData || {};

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  if (!externalData || Object.keys(externalData).length === 0) {
    return null;
  }

  if (ticket.site === 'tournament') {
    const tabs = [
      { id: 'user', label: 'Пользователь' },
      { id: 'player', label: 'Игровой аккаунт' },
      { id: 'statistics', label: 'Турнирная статистика' },
      { id: 'teams', label: 'История команд' },
      { id: 'tickets', label: 'История обращений' },
    ];

    const activeTabLabel = tabs.find(t => t.id === activeTab)?.label || tabs[0].label;

    return (
      <S.TabsContainer>
        <S.TabsDropdown ref={dropdownRef}>
          <S.TabsDropdownButton
            $active={isDropdownOpen}
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
          >
            {activeTabLabel}
          </S.TabsDropdownButton>
          <S.TabsDropdownMenu $isOpen={isDropdownOpen}>
            {tabs.map(tab => (
              <S.TabsDropdownItem
                key={tab.id}
                $active={activeTab === tab.id}
                onClick={() => {
                  setActiveTab(tab.id);
                  setIsDropdownOpen(false);
                }}
              >
                {tab.label}
              </S.TabsDropdownItem>
            ))}
          </S.TabsDropdownMenu>
        </S.TabsDropdown>
        <S.TabsHeaderWrapper>
          <S.TabsHeader>
            {tabs.map(tab => (
              <S.TabButton
                key={tab.id}
                $active={activeTab === tab.id}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.label}
              </S.TabButton>
            ))}
          </S.TabsHeader>
        </S.TabsHeaderWrapper>
        <S.TabContent>
          {activeTab === 'user' && <TournamentUserTab data={externalData} />}
          {activeTab === 'player' && <TournamentPlayerProfileTab data={externalData} />}
          {activeTab === 'statistics' && <TournamentStatisticsTab data={externalData} />}
          {activeTab === 'teams' && <TournamentTeamsTab data={externalData} />}
          {activeTab === 'tickets' && <TournamentTicketsTab ticket={ticket} />}
        </S.TabContent>
      </S.TabsContainer>
    );
  }

  if (ticket.site === 'hudle') {
    const tabs = [
      { id: 'user', label: 'Пользователь' },
      { id: 'servers', label: 'Сервера' },
      { id: 'channels', label: 'Каналы' },
      { id: 'friends', label: 'Друзья' },
      { id: 'tickets', label: 'История тикетов' },
      { id: 'activity', label: 'Активность' },
      { id: 'ip', label: 'IP адреса' },
    ];

    const activeTabLabel = tabs.find(t => t.id === activeTab)?.label || tabs[0].label;

    return (
      <S.TabsContainer>
        <S.TabsDropdown ref={dropdownRef}>
          <S.TabsDropdownButton
            $active={isDropdownOpen}
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
          >
            {activeTabLabel}
          </S.TabsDropdownButton>
          <S.TabsDropdownMenu $isOpen={isDropdownOpen}>
            {tabs.map(tab => (
              <S.TabsDropdownItem
                key={tab.id}
                $active={activeTab === tab.id}
                onClick={() => {
                  setActiveTab(tab.id);
                  setIsDropdownOpen(false);
                }}
              >
                {tab.label}
              </S.TabsDropdownItem>
            ))}
          </S.TabsDropdownMenu>
        </S.TabsDropdown>
        <S.TabsHeaderWrapper>
          <S.TabsHeader>
            {tabs.map(tab => (
              <S.TabButton
                key={tab.id}
                $active={activeTab === tab.id}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.label}
              </S.TabButton>
            ))}
          </S.TabsHeader>
        </S.TabsHeaderWrapper>
        <S.TabContent>
          {activeTab === 'user' && <HudleUserTab data={externalData} />}
          {activeTab === 'servers' && <HudleServersTab data={externalData} />}
          {activeTab === 'channels' && <HudleChannelsTab data={externalData} />}
          {activeTab === 'friends' && <HudleFriendsTab data={externalData} />}
          {activeTab === 'tickets' && <HudleTicketsTab ticket={ticket} />}
          {activeTab === 'activity' && <HudleActivityTab data={externalData} />}
          {activeTab === 'ip' && <HudleIpAddressesTab data={externalData} />}
        </S.TabContent>
      </S.TabsContainer>
    );
  }

  return null;
};

